import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.1]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.65], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden flex items-end bg-ink">
      {/* Finished vehicle visual — swappable without redesign */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute right-0 top-0 h-full w-full md:w-[48%]" aria-hidden="true">
        <img
          src="/assets/hero-clean.webp"
          alt=""
          className="w-full h-full object-cover object-center opacity-50 md:opacity-100"
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/55 md:via-ink/35 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />
      </motion.div>

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 pb-20 sm:pb-24 pt-32"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="font-accent text-[10px] sm:text-[11px] tracking-[0.4em] uppercase text-silver/70 mb-7"
          data-testid="hero-overline"
        >
          Mobile Vehicle Care · Kansas City
        </motion.p>

        <h1 className="select-none" aria-label="DARK Mobile Detail">
          <span className="block overflow-hidden leading-[0.9]">
            <span className="flex">
              {"DARK".split("").map((ch, i) => (
                <motion.span
                  key={i}
                  initial={{ y: "112%" }}
                  animate={{ y: "0%" }}
                  transition={{ delay: 0.2 + i * 0.09, duration: 0.95, ease: EASE }}
                  className="font-display font-semibold metallic-text text-[24vw] sm:text-[16vw] lg:text-[9.5rem] tracking-[0.02em] inline-block"
                >
                  {ch}
                </motion.span>
              ))}
            </span>
          </span>
          <motion.span
            initial={{ opacity: 0, letterSpacing: "0.2em" }}
            animate={{ opacity: 1, letterSpacing: "0.5em" }}
            transition={{ delay: 0.85, duration: 1.1, ease: EASE }}
            className="font-accent text-volt text-xs sm:text-sm uppercase inline-block mt-4"
            data-testid="hero-wordmark-sub"
          >
            Mobile Detail
          </motion.span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.9, ease: EASE }}
          className="font-display italic text-2xl sm:text-3xl lg:text-4xl text-ivory mt-10 max-w-xl leading-snug"
          data-testid="hero-tagline"
        >
          Vehicle care for people with a standard.
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.9, ease: EASE }}
          className="text-silver/75 text-sm sm:text-base mt-5 max-w-md leading-relaxed"
        >
          Restoration, maintenance, and membership care delivered to your home or workplace.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.55, duration: 0.9, ease: EASE }}
          className="flex flex-col sm:flex-row gap-4 mt-11"
        >
          <button
            data-testid="hero-cta-request-service"
            onClick={() => scrollToId("request")}
            className="cta-shadow font-accent text-xs tracking-[0.25em] uppercase bg-volt text-white px-9 py-4 hover:bg-[#4A8FFF] transition-colors duration-300"
          >
            Request Service
          </button>
          <button
            data-testid="hero-cta-explore-membership"
            onClick={() => scrollToId("membership")}
            className="font-accent text-xs tracking-[0.25em] uppercase border border-silver/25 text-silver px-9 py-4 hover:border-volt hover:text-ivory transition-colors duration-300"
          >
            Explore Membership
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.85, duration: 1 }}
          className="font-accent text-[10px] tracking-[0.3em] uppercase text-silver/45 mt-9"
          data-testid="hero-area-note"
        >
          <span className="inline-block w-5 h-px bg-volt align-middle mr-3" />
          Kansas City Metro · Kansas &amp; Missouri
        </motion.p>
      </motion.div>
    </section>
  );
};
